import React, { useCallback, useEffect, useMemo, useState } from 'react';
import adminService from '../../services/adminService';
import { useDialog } from '../../context/DialogContext';
import { useUser } from '../../context/UserContext';
import { AdminModal, Btn, CheckBox, Chip, DataTable, Field, FieldLabel, SearchField, inputClass, shortDate } from './shell';
import InviteCardSheet from './InviteCardSheet';

/**
 * ENGINES › PRO PAGES — spec/sections.md §21. Every Pro page, claimed or listed.
 *
 * Deliberately narrow: look, unpublish, delete, payout terms. A claimed page belongs to a real
 * business — editing their copy or prices from an admin screen would be editing
 * their business, so that isn't offered here.
 *
 * Gates (`pro_pages`), absent not disabled: UNPUBLISH / PUBLISH / PAYOUT TERMS → edit; DELETE → delete,
 * and not rendered at all once a page has bookings (they carry escrow and payout
 * history — the server refuses too); the row says so instead.
 */
const FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'claimed', label: 'CLAIMED' },
  { id: 'listed', label: 'LISTED' },
  { id: 'published', label: 'PUBLISHED' },
  { id: 'unpublished', label: 'UNPUBLISHED' },
  { id: 'demo', label: 'DEMO' },
];
const PAGE_SIZE = 20;
const MAX_HOLD_HOURS = 24 * 90;

/** The PAYOUT column: what a page's new bookings get. */
export const payoutLabel = (row) => (row.instantPayout ? 'INSTANT' : `${row.escrowHoldHours ?? 24}H HOLD`);

/** PAYOUT TERMS: how a page's booking money reaches its owner. New bookings only. */
export function ProPayoutTermsModal({ page, onClose, onSaved }) {
  const [instant, setInstant] = useState(false);
  const [hours, setHours] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (page) { setInstant(!!page.instantPayout); setHours(page.escrowHoldHours == null ? '' : String(page.escrowHoldHours)); setError(''); } }, [page]);
  if (!page) return null;
  const save = async () => {
    const trimmed = hours.trim();
    let h = null;
    if (trimmed !== '') {
      h = Number(trimmed);
      if (!Number.isInteger(h) || h < 0 || h > MAX_HOLD_HOURS) { setError(`Whole hours from 0 to ${MAX_HOLD_HOURS}, or empty for the standard 24.`); return; }
    }
    setSaving(true);
    const r = await adminService.setProPayoutTerms(page.id, { instantPayout: instant, escrowHoldHours: h });
    setSaving(false);
    if (r.success) onSaved({ id: page.id, instantPayout: instant, escrowHoldHours: h }); else setError(r.error || 'Could not save.');
  };
  return (
    <AdminModal open onClose={onClose} title="PAYOUT TERMS" kicker={`How ${page.name}'s booking money reaches its owner. Bookings already paid keep the terms they were paid under.`}
      footer={<><Btn size="sheet" onClick={onClose}>CANCEL</Btn><Btn size="sheet" variant="solid" onClick={save} disabled={saving}>{saving ? 'SAVING…' : 'SAVE TERMS'}</Btn></>}>
      <div className="flex flex-col gap-4" data-testid="payout-terms">
        <div className="rounded-[18px] bg-[#F7F7F7] p-4">
          <CheckBox checked={instant} onChange={setInstant} label="INSTANT PAYOUT" />
          <p className="m-0 pt-2 font-mono text-[11px] leading-[1.6] text-[#6B7280]">The money is theirs to cash out as soon as the client pays, before any work is done. The client can&apos;t raise a problem in the app. If the client charges back after it has been cashed out, Portal takes the loss.</p>
        </div>
        <Field label="ESCROW HOLD · HOURS AFTER A GIG OR SESSION ENDS" error={error}>
          <input type="number" min="0" max={MAX_HOLD_HOURS} step="1" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="24" className={inputClass} aria-label="Escrow hold in hours" />
        </Field>
        <FieldLabel>{instant ? 'NOT USED WHILE INSTANT PAYOUT IS ON.' : 'EMPTY IS THE STANDARD 24. 0 RELEASES ON THE FIRST SWEEP AFTER IT ENDS.'}</FieldLabel>
      </div>
    </AdminModal>
  );
}

export default function ProPagesPanel() {
  const { showConfirm, showAlert } = useDialog();
  const { can } = useUser();
  const canEdit = can('pro_pages', 'edit');
  const canDelete = can('pro_pages', 'delete');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [termsFor, setTermsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getPros({ q, filter });
    if (r.success) setRows(r.data || []);
    else await showAlert({ title: 'COULD NOT LOAD', message: r.error || 'Could not load Pro pages.', type: 'error', theme: 'admin' });
    setLoading(false);
  }, [q, filter, showAlert]);

  // Debounced so typing a search doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { setPage(1); }, [q, filter]);

  const togglePublished = async (row) => {
    setBusyId(row.id);
    const r = await adminService.setProPublished(row.id, !row.isPublished);
    setBusyId(null);
    if (!r.success) { await showAlert({ title: 'FAILED', message: r.error || 'Could not change that.', type: 'error', theme: 'admin' }); return; }
    setRows((cur) => cur.map((x) => (x.id === row.id ? { ...x, isPublished: !x.isPublished } : x)));
  };

  const remove = async (row) => {
    const yes = await showConfirm({
      title: `DELETE ${row.name.toUpperCase()}?`,
      message: row.isDemo
        ? 'This is a demo fixture. Deleting removes the page and everything on it.'
        : 'This removes the page, its photos, services, team and availability. This cannot be undone — unpublish instead if you only want it off the directory.',
      confirmLabel: 'DELETE',
      cancelLabel: 'CANCEL',
      variant: 'danger',
      theme: 'admin',
    });
    if (!yes) return;
    setBusyId(row.id);
    const r = await adminService.deletePro(row.id);
    setBusyId(null);
    if (!r.success) { await showAlert({ title: 'FAILED', message: r.error || 'Could not delete that page.', type: 'error', theme: 'admin' }); return; }
    setRows((cur) => cur.filter((x) => x.id !== row.id));
  };

  const pageRows = useMemo(() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [rows, page]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={q} onChange={setQ} placeholder="SEARCH NAME, SLUG OR CITY" />
        {FILTERS.map((f) => (
          <Chip key={f.id} selected={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}</Chip>
        ))}
      </div>

      <DataTable
        columns={[
          {
            key: 'name', label: 'PAGE', fr: 2,
            render: (row) => (
              <>
                <a href={`/pro/${row.slug}`} target="_blank" rel="noreferrer" className="hover:underline">{row.name}</a>
                {row.isDemo && <span className="font-normal text-[#6B7280]"> · DEMO</span>}
                {row.isCharity && <span className="font-normal text-[#6B7280]"> · CHARITY</span>}
                {canDelete && row.bookingCount > 0 && <span className="font-normal text-[#6B7280]"> · HAS BOOKINGS — UNPUBLISH INSTEAD OF DELETING</span>}
              </>
            ),
          },
          { key: 'crafts', label: 'CRAFT', fr: 1.4, render: (row) => (row.crafts ? row.crafts.split(',').slice(0, 3).join(', ') : (row.kind || '—')) },
          { key: 'isListed', label: 'KIND', fr: 1, render: (row) => (row.isListed ? 'LISTED' : 'CLAIMED') },
          { key: 'bookingCount', label: 'BOOKINGS', fr: 1, right: true, render: (row) => row.bookingCount ?? 0 },
          { key: 'payout', label: 'PAYOUT', fr: 0.9, render: payoutLabel },
          { key: 'isPublished', label: 'STATUS', fr: 0.9, render: (row) => (row.isPublished ? 'PUBLISHED' : 'UNPUBLISHED') },
          { key: 'createdAt', label: 'CREATED', fr: 0.9, render: (row) => shortDate(row.createdAt) },
        ]}
        rows={pageRows}
        loading={loading}
        empty="NO PRO PAGES MATCH."
        hasActions={canEdit || canDelete}
        actions={(row) => {
          if (busyId === row.id) return [];
          const acts = [];
          if (canEdit) acts.push({ label: row.isPublished ? 'UNPUBLISH' : 'PUBLISH', onClick: () => togglePublished(row) });
          if (canEdit && !row.isListed) acts.push({ label: 'PAYOUT TERMS', onClick: () => setTermsFor(row) });
          if (canEdit && !row.isListed) acts.push({ label: 'INVITE CARD', onClick: () => setInviteTarget({ kind: 'pro', id: row.id, name: row.name }) });
          if (canDelete && !(row.bookingCount > 0)) acts.push({ label: 'DELETE', variant: 'danger', onClick: () => remove(row) });
          return acts;
        }}
        total={rows.length}
        page={page}
        pageSize={PAGE_SIZE}
        onPage={setPage}
        footerLeft={`${rows.length.toLocaleString()} page${rows.length === 1 ? '' : 's'}`}
      />
      <InviteCardSheet target={inviteTarget} onClose={() => setInviteTarget(null)} />
      <ProPayoutTermsModal page={termsFor} onClose={() => setTermsFor(null)}
        onSaved={({ id, ...terms }) => { setTermsFor(null); setRows((cur) => cur.map((x) => (x.id === id ? { ...x, ...terms } : x))); }} />
    </div>
  );
}
